import React, { useState, useCallback, memo } from 'react';
import { Box, Link, Typography, Collapse } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Icon } from '@alycecom/ui';

import Avatar from '../../../../../components/Avatar/Avatar';
import { formatPositionAtCompany } from '../../../helpers/recipient.helpers';
import { TFullSender } from '../../../../../store/users/users.types';

import FunFacts from './FunFacts';

const useStyles = makeStyles(({ spacing }) => ({
  avatar: {
    width: 64,
    height: 64,
    marginRight: spacing(1),
  },
  collapse: {
    width: '100%',
  },
  info: {
    maxWidth: 165,
    wordBreak: 'break-word',
  },
}));

export interface ISenderInfoMobileProps {
  sender: TFullSender;
  onEmailClick: () => void;
  onPhoneClick: () => void;
  onOpenFunFacts: () => void;
}

const SenderInfoMobile = ({ sender, onEmailClick, onPhoneClick, onOpenFunFacts }: ISenderInfoMobileProps) => {
  const classes = useStyles();

  const jobDesc = formatPositionAtCompany(sender);

  const [isFactsOpen, setIsFactsOpen] = useState(false);

  const handleToggleFacts = useCallback(() => {
    setIsFactsOpen(!isFactsOpen);
    if (!isFactsOpen) {
      onOpenFunFacts();
    }
  }, [isFactsOpen, onOpenFunFacts]);

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" width={1}>
        <Box display="flex" justifyContent="flex-start" alignItems="flex-start">
          <Avatar className={classes.avatar} src={sender.image} alt="Sender avatar" />
          <Box className={classes.info}>
            <Box className="Body-Regular-Left-Chambray-Bold" data-testid="giftSenderName" textAlign="left">
              {sender.fullName}
            </Box>
            <Box mb={0.5} textAlign="left" data-testid="giftSenderPosition">
              <Typography className="Body-Small-Static">{jobDesc}</Typography>
            </Box>
            <Box mb={0.5} data-testid="giftSenderEmail" textAlign="left">
              <Link
                onClick={onEmailClick}
                className="Body-Small-Link"
                href={`mailto:${sender.email}`}
                underline="hover"
              >
                {sender.email}
              </Link>
            </Box>
            {sender.phone && (
              <Box data-testid="giftSenderPhone" textAlign="left">
                <Link onClick={onPhoneClick} className="Body-Small-Link" href={`tel:${sender.phone}`} underline="hover">
                  {sender.phone}
                </Link>
              </Box>
            )}
          </Box>
        </Box>
        {sender.funFacts && (
          <Box
            data-testid="SenderInfoMobile-FunFactsButton"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width={80}
            mt={1}
            onClick={handleToggleFacts}
          >
            <Typography className="Body-Small-Link">{isFactsOpen ? 'View less' : 'View more'}</Typography>
            <Icon icon={isFactsOpen ? 'chevron-up' : 'chevron-down'} color="link.main" />
          </Box>
        )}
      </Box>
      {sender.funFacts && (
        <Collapse classes={{ root: classes.collapse }} in={isFactsOpen}>
          <FunFacts facts={sender.funFacts} />
        </Collapse>
      )}
    </>
  );
};

export default memo(SenderInfoMobile);
